import { memo, useContext, lazy } from 'react';
import { formatCurrency } from '@atc/string-fns';
import { useElementObserver } from '@atc/use-element-observer';
import { useFeatures } from '@atc/features';
import { ratesLabels } from 'axl-config';
import ndsConstants from '../ndsConstants.js';
import CalculatorInput from './CalculatorInput.js';
import TradeInfoInputs from './TradeInfoInputs.js';
import ConfigContext from '../../ConfigContext/ConfigContext.js';
import TermSelect from './TermSelect.js';
import { jsx, jsxs } from 'react/jsx-runtime';
const SelectButtonContainer = /*#__PURE__*/lazy(() => import(/* webpackPrefetch: true */
/* webpackChunkName: "SelectButtonContainer" */
'./SelectButtonContainer.js'));
const {
  loanTermOptions,
  loanTermOptionsExcellentTier
} = ndsConstants;

/**
 *  Finance tab of Native Deal Calculator
 */

function FinanceTab(_ref) {
  let {
    lendingOptions,
    creditTierOptions,
    creditVerificationLink,
    customDefaultFinanceTerm,
    onSupportingLinkClick,
    isSupportingLinkClicked,
    financeDownPayment,
    formattedTotalPrice,
    handleNDSClick,
    handleSelectChange,
    inputProps,
    previousCompletedInput,
    renderPriceBreakdown,
    renderTaxesAndFees,
    selectedCreditTier,
    selectedLoanTerm,
    showOfferPrice,
    tradeInfoInputProps
  } = _ref;
  const {
    disableTrade,
    enableCreditVerification,
    enableNegotiation,
    oemLabels = {},
    showRatesTable,
    selectedCriteria: {
      creditLowScore,
      financeTermOptions = []
    } = {}
  } = useContext(ConfigContext) ?? {};
  const {
    cashDownLabelDS
  } = oemLabels;
  const {
    credit_verification: [creditVerificationEnabled],
    custom_loan_term: [isCustomLoanTermEnabled]
  } = useFeatures(['credit_verification', 'custom_loan_term']);
  const showCreditVerificationCTA = enableCreditVerification || creditVerificationEnabled;
  const [containerElement, containerRef] = useElementObserver();
  const negotiableOfferLabel = showOfferPrice ? 'Offer Price' : 'Listing Price';

  /*
   * Populate finance terms
   */
  let addedLoanTerms;
  if (financeTermOptions.length > 0) {
    // Use dynamic terms provided by selectedCriteria
    addedLoanTerms = financeTermOptions;
  } else {
    // Fall back to default hardcoded terms
    addedLoanTerms = selectedCreditTier === 'excellent' ? loanTermOptionsExcellentTier : loanTermOptions;
  }
  if (
  // Add custom loan term if enabled and not already included
  isCustomLoanTermEnabled && customDefaultFinanceTerm !== null && customDefaultFinanceTerm > 0 && !addedLoanTerms.includes(customDefaultFinanceTerm)) {
    addedLoanTerms = [...addedLoanTerms, customDefaultFinanceTerm].sort((a, b) => a - b);
  }
  if (creditLowScore && creditTierOptions?.length) {
    // replace the three-digit low credit score only
    creditTierOptions[4].label = creditTierOptions[4].label.replace(/\((\d+)-(\d+)\)/, (_, __, highScore) => `(${creditLowScore}-${highScore})`);
  }
  const creditRatingChildren = /*#__PURE__*/jsx(TermSelect, {
    name: "creditTierInput",
    id: "creditTierInput-finance",
    label: "Credit Rating",
    labelClassName: "display-none display-sm-block text-size-300",
    value: selectedCreditTier,
    options: creditTierOptions,
    handleChange: handleSelectChange,
    supportingLink: showCreditVerificationCTA && creditVerificationLink
  });
  const downPaymentChildren = /*#__PURE__*/jsx(CalculatorInput, {
    name: "downPaymentInput",
    label: cashDownLabelDS ?? 'Down Payment',
    inputProps: inputProps,
    value: financeDownPayment,
    supportingLink: false,
    tabName: "finance"
  }, "calculatorInput");
  const listingPriceChildren = /*#__PURE__*/jsx(CalculatorInput, {
    name: "offerPriceInput",
    label: negotiableOfferLabel,
    inputProps: {
      ...inputProps,
      className: 'margin-bottom-4'
    },
    value: formattedTotalPrice,
    supportingLink: false,
    tabName: "finance",
    showTextField: !enableNegotiation
  }, "offerPriceInput");
  const tradeInChildren = !disableTrade && /*#__PURE__*/jsx(TradeInfoInputs, {
    ...tradeInfoInputProps,
    inputProps: inputProps,
    tabName: "finance"
  });
  const termsChildren = /*#__PURE__*/jsx(TermSelect, {
    value: selectedLoanTerm,
    options: addedLoanTerms,
    name: "termInput",
    label: "Finance Terms",
    labelClassName: "display-none display-sm-block text-size-300",
    handleChange: handleSelectChange,
    rates: showRatesTable ? lendingOptions : undefined
  });

  // Add inputs props for the new design here.
  const calcInputs = [{
    heading: 'Finance Terms',
    primaryLabel: 'Finance Terms',
    children: termsChildren,
    secondaryLabel: `${selectedLoanTerm} months`,
    containerElement,
    inputCompletedState: previousCompletedInput === 'termInput',
    analyticProps: {
      cmp: 'nds_finance-term',
      userModified: 'FINANCE_TERM_LENGTH_CHANGED'
    },
    'data-cmp': 'terms'
  }, {
    heading: 'Credit Rating',
    primaryLabel: 'Credit Rating',
    children: creditRatingChildren,
    secondaryLabel: ratesLabels[selectedCreditTier]?.label,
    position: 'right',
    containerElement,
    inputCompletedState: previousCompletedInput === 'creditTierInput',
    analyticProps: {
      cmp: 'nds_credit-rating',
      userModified: 'CREDIT_VERIFICATION_CLICKED'
    },
    onSupportingLinkClick,
    isSupportingLinkClicked,
    'data-cmp': 'creditRating'
  }];
  const tradeInCalcInput = {
    analyticProps: {
      cmp: 'nds_trade-in',
      userModified: 'TRADE_IN_VALUE_CHANGED'
    },
    children: tradeInChildren,
    containerElement,
    'data-cmp': 'tradeIn',
    heading: 'Trade-In',
    inputCompletedState: previousCompletedInput === 'tradeInInput',
    position: 'right',
    primaryLabel: 'Trade-In',
    secondaryLabel: formatCurrency(tradeInfoInputProps.valueTradeIn - tradeInfoInputProps.valueOwed || 0)
  };

  // Add trade-in to the front of the array if trade is not disabled
  if (!disableTrade) {
    calcInputs.unshift(tradeInCalcInput);
  }
  return /*#__PURE__*/jsxs("div", {
    className: "margin-top-5",
    children: [renderPriceBreakdown('finance'), listingPriceChildren, /*#__PURE__*/jsxs("div", {
      className: "row margin-bottom-1",
      ref: containerRef,
      children: [/*#__PURE__*/jsx("div", {
        className: disableTrade ? 'col-xs-12' : 'col-xs-6',
        children: downPaymentChildren
      }), /*#__PURE__*/jsx(SelectButtonContainer, {
        handleNDSClick: handleNDSClick,
        calcInputs: calcInputs
      })]
    }), renderTaxesAndFees('finance')]
  });
}
var FinanceTab$1 = /*#__PURE__*/memo(FinanceTab);
export { FinanceTab$1 as default };
